import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, Trash2, Sparkles, ShoppingCart } from 'lucide-react';
import Modal from '../components/common/Modal';
import NotificationSystem, { useNotification } from '../components/common/NotificationSystem';
import CustomSelect from '../components/common/CustomSelect';
import proposalService from '../services/proposalService';
import clientService from '../services/clientService';

const ProposalListPage = () => {
  const navigate = useNavigate();
  const { notification, showNotification, hideNotification } = useNotification();

  const [proposals, setProposals] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ status: '', client: '' });

  // Modal de creación
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({ client: '', title: '', requirements: '' });

  // Modal de eliminación
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [proposalToDelete, setProposalToDelete] = useState(null);

  const loadClients = useCallback(async () => {
    try {
      const data = await clientService.getClients({ limit: 200, isActive: true });
      setClients(data.data?.clients || data.clients || []);
    } catch (error) {
      console.error('Error cargando clientes:', error);
    }
  }, []);

  const loadProposals = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const data = await proposalService.getProposals({
        page,
        limit: itemsPerPage,
        search: searchTerm,
        status: filters.status,
        client: filters.client
      });
      setProposals(data.proposals || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalItems(data.pagination?.totalItems || 0);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error cargando propuestas:', error);
      showNotification('Error al cargar las propuestas', 'error');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters, itemsPerPage, showNotification]);

  useEffect(() => {
    loadClients();
    loadProposals(1);
  }, [loadProposals, loadClients]);

  useEffect(() => {
    loadProposals(1);
  }, [searchTerm, filters]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const data = await proposalService.createProposal(formData);
      showNotification(data.message, 'success');
      setShowCreateModal(false);
      setFormData({ client: '', title: '', requirements: '' });
      // Navegar directamente al detalle para arrancar con IA
      if (data.proposal?._id) {
        navigate(`/proposals/${data.proposal._id}`);
      } else {
        loadProposals();
      }
    } catch (error) {
      showNotification(error.message || 'Error al crear la propuesta', 'error');
    }
  };

  const confirmDelete = (proposal) => {
    setProposalToDelete(proposal);
    setShowDeleteModal(true);
  };

  const executeDelete = async () => {
    if (!proposalToDelete) return;
    try {
      await proposalService.deleteProposal(proposalToDelete._id);
      showNotification('Propuesta eliminada exitosamente', 'success');
      setShowDeleteModal(false);
      setProposalToDelete(null);
      loadProposals();
    } catch (error) {
      showNotification(error.message || 'Error al eliminar', 'error');
    }
  };

  const statusLabels = {
    DRAFT: { label: 'Borrador', class: 'badge-secondary' },
    IN_REVIEW: { label: 'En Revisión', class: 'badge-primary' },
    APPROVED: { label: 'Aprobada', class: 'badge-success' },
    CONVERTED_TO_SALE: { label: 'Convertida', class: 'badge-success' },
    REJECTED: { label: 'Rechazada', class: 'badge-secondary' }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) loadProposals(newPage);
  };

  return (
    <div className="animate-fadeIn">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">🤖 Propuestas Inteligentes</h1>
          <p className="text-gray-600">Crea propuestas técnicas y comerciales asistidas por Inteligencia Artificial</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
          <Plus size={16} /> Nueva Propuesta IA
        </button>
      </div>

      {/* Filtros */}
      <div className="card mb-6 filters-container">
        <div className="card-content">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-64">
              <label className="form-label">Buscar</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Título o requerimientos..."
                className="form-input"
              />
            </div>
            <div className="w-48">
              <CustomSelect
                label="Estado"
                value={filters.status}
                onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                options={[
                  { value: '', label: 'Todos' },
                  { value: 'DRAFT', label: 'Borrador' },
                  { value: 'IN_REVIEW', label: 'En Revisión' },
                  { value: 'APPROVED', label: 'Aprobada' },
                  { value: 'CONVERTED_TO_SALE', label: 'Convertida' },
                  { value: 'REJECTED', label: 'Rechazada' }
                ]}
                placeholder="Todos"
              />
            </div>
            <div className="w-48">
              <CustomSelect
                label="Cliente"
                value={filters.client}
                onChange={(value) => setFilters(prev => ({ ...prev, client: value }))}
                options={[
                  { value: '', label: 'Todos los clientes' },
                  ...clients.map(c => ({ value: c._id, label: c.name }))
                ]}
                placeholder="Todos los clientes"
              />
            </div>
            <div className="w-48">
              <label className="form-label">&nbsp;</label>
              <button onClick={() => { setSearchTerm(''); setFilters({ status: '', client: '' }); }} className="btn btn-outline w-full">
                🗑️ Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="table-container">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="loading-spinner"></div>
            <p className="mt-4 text-gray-600">Cargando propuestas...</p>
          </div>
        ) : proposals.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-6xl mb-4">🤖</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay propuestas</h3>
            <p className="text-gray-600 mb-6">Crea tu primera propuesta asistida por IA</p>
            <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
              <Plus size={16} /> Nueva Propuesta IA
            </button>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Título</th>
                <th>Cliente</th>
                <th>Estado</th>
                <th>Total Estimado</th>
                <th>Creada</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {proposals.map(proposal => (
                <tr key={proposal._id}>
                  <td>
                    <div className="font-medium text-gray-900">{proposal.title}</div>
                    <div className="text-sm text-gray-500" style={{
                      maxWidth: '250px', display: '-webkit-box', WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical', overflow: 'hidden'
                    }}>
                      {proposal.requirements}
                    </div>
                  </td>
                  <td className="text-sm text-gray-700">{proposal.client?.name || 'N/A'}</td>
                  <td>
                    <span className={`badge ${statusLabels[proposal.status]?.class || 'badge-secondary'}`}>
                      {statusLabels[proposal.status]?.label || proposal.status}
                    </span>
                  </td>
                  <td className="text-sm text-gray-700">
                    {proposal.commercialOffer?.total ? `$${proposal.commercialOffer.total.toLocaleString()}` : '—'}
                  </td>
                  <td className="text-sm text-gray-700">
                    {new Date(proposal.createdAt).toLocaleDateString()}
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/proposals/${proposal._id}`)}
                        className="btn btn-primary btn-sm"
                        title="Ver / Editar con IA"
                      >
                        <Sparkles size={16} />
                      </button>
                      {proposal.status !== 'CONVERTED_TO_SALE' && proposal.status !== 'REJECTED' && proposal.currentStage !== 'COMPLETED' && (
                        <button
                          onClick={() => confirmDelete(proposal)}
                          className="btn btn-danger btn-sm"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            <span>Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems}</span>
          </div>
          <div className="pagination-controls">
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="btn btn-outline btn-sm">← Anterior</button>
            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="btn btn-outline btn-sm">Siguiente →</button>
          </div>
        </div>
      )}

      {/* Modal Crear */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="🤖 Nueva Propuesta Inteligente"
        onSubmit={handleCreate}
        submitText="Crear y Abrir con IA"
      >
        <div className="form-section">
          <h4 className="form-section-title">Información Básica</h4>
          <CustomSelect
            label="Cliente"
            value={formData.client}
            onChange={(value) => setFormData(prev => ({ ...prev, client: value }))}
            options={[
              { value: '', label: 'Seleccionar cliente' },
              ...clients.map(c => ({ value: c._id, label: c.name }))
            ]}
            placeholder="Seleccionar cliente"
          />
          <div className="form-group" style={{ marginTop: '12px' }}>
            <label className="form-label">Título de la Propuesta <span className="label-required">*</span></label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="form-input"
              placeholder="Ej. Desarrollo de Plataforma E-Commerce"
              required
            />
          </div>
          <div className="form-group" style={{ marginTop: '12px' }}>
            <label className="form-label">Requerimientos del Cliente <span className="label-required">*</span></label>
            <textarea
              value={formData.requirements}
              onChange={(e) => setFormData(prev => ({ ...prev, requirements: e.target.value }))}
              className="form-input form-textarea"
              placeholder="Pega aquí el levantamiento de requerimientos, notas de la reunión, o la descripción del proyecto que deseas cotizar..."
              rows="8"
              required
            />
          </div>
        </div>
      </Modal>

      {/* Modal Eliminar */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirmar Eliminación"
        onSubmit={(e) => { e.preventDefault(); executeDelete(); }}
        submitText="Eliminar"
        submitButtonClass="btn-danger"
      >
        <div className="p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
          <p className="font-medium text-lg mb-2">¿Eliminar esta propuesta?</p>
          <p>Se eliminará: <strong>{proposalToDelete?.title}</strong></p>
        </div>
      </Modal>

      <NotificationSystem notification={notification} onClose={hideNotification} />
    </div>
  );
};

export default ProposalListPage;
